import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StatsBand } from "@/components/StatsBand";
import { FAQSection } from "@/components/FAQSection";
import { SERVICES_PAGE, SERVICES_FAQ } from "@/data/servicesContent";

/* Scroll-reveal wrapper (mirrors the ss3-rv behavior deployed to WP) */
const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setOn(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setOn(true), obs.disconnect())),
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${on ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const ServiceCard = ({ icon: Icon, title, line, testId }) => (
  <div
    data-testid={testId}
    className="h-full rounded-[12px] border border-[#ececec] bg-white p-6 shadow-[0_6px_18px_rgba(2,2,2,0.04)] hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] hover:border-[#ee2e24]/30 transition-transform duration-200"
  >
    <div className="h-11 w-11 rounded-[10px] bg-[#ee2e24]/[0.08] flex items-center justify-center">
      <Icon size={20} strokeWidth={1.75} className="text-[#ee2e24]" />
    </div>
    <h3 className="mt-4 font-nunito text-[15px] font-extrabold text-[#383838]">{title}</h3>
    <p className="mt-2 text-[13px] text-[#6e6e6e] leading-relaxed">{line}</p>
  </div>
);

export default function ServicesPage() {
  const S = SERVICES_PAGE;
  return (
    <div data-testid="services-page" className="bg-white">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section data-testid="services-hero" className="relative overflow-hidden bg-[#0b0b0c] hero-noise">
          <img src={S.hero.bg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(2,2,2,0.85) 0%, rgba(2,2,2,0.62) 50%, rgba(2,2,2,0.85) 100%)" }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 min-h-[500px] md:min-h-[540px] flex items-center justify-center pt-32 pb-16 md:pt-36 md:pb-20">
            <div className="text-center max-w-3xl mx-auto">
              <p className="font-nunito text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.3em] text-[#ff6b63] mb-5">
                {S.hero.eyebrow}
              </p>
              <h1
                data-testid="services-hero-title"
                className="font-nunito font-extrabold text-white text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight"
              >
                {S.hero.titlePre}
                <span className="text-[#ee2e24]">{S.hero.titleAccent}</span>
              </h1>
              <p className="mt-5 text-[#dedede] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                {S.hero.sub}
              </p>
            </div>
          </div>
        </section>

        {/* Flagship four */}
        <section data-testid="services-flagship" className="bg-[#fafafa] border-b border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
                {S.flagship.eyebrow}
              </p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul-center">
                {S.flagship.heading}
              </h2>
              <p className="mt-5 text-[15px] text-[#6e6e6e] leading-relaxed">{S.flagship.sub}</p>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {S.flagship.items.map((item, i) => (
                <Reveal key={item.title} delay={i * 80}>
                  <ServiceCard {...item} testId={`services-flagship-card-${i + 1}`} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Full catalog by discipline */}
        <section data-testid="services-catalog" className="bg-white py-16 md:py-22">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="max-w-3xl">
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
                {S.catalog.eyebrow}
              </p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul">
                {S.catalog.heading}
              </h2>
              <p className="mt-5 text-[15px] text-[#6e6e6e] leading-relaxed">{S.catalog.sub}</p>
            </div>
            {S.catalog.groups.map((group, gi) => (
              <div key={group.key} data-testid={`services-group-${group.key}`} className="mt-14">
                <Reveal>
                  <div className="flex items-center gap-4">
                    <span className="shrink-0 h-9 w-9 rounded-full bg-[#ee2e24]/[0.08] border border-[#ee2e24]/25 flex items-center justify-center font-nunito text-[12px] font-extrabold text-[#ee2e24]">
                      {String(gi + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-nunito text-xl font-extrabold text-[#383838]">{group.name}</h3>
                    <span className="hidden sm:block h-px flex-1 bg-[#ececec]" aria-hidden="true" />
                    <p className="hidden sm:block text-[13px] text-[#8a8a8a] italic">{group.tagline}</p>
                  </div>
                </Reveal>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.items.map((item, i) => (
                    <Reveal key={item.title} delay={i * 70}>
                      <ServiceCard {...item} testId={`services-card-${group.key}-${i + 1}`} />
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Machine band */}
        <section data-testid="services-machine" className="bg-[#0b0b0c] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ff6b63]">
                {S.machine.eyebrow}
              </p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-white">{S.machine.heading}</h2>
              <p className="mt-5 text-[15px] text-[#dedede] leading-relaxed">{S.machine.sub}</p>
            </div>
            <div className="space-y-4">
              {S.machine.points.map((p, i) => {
                const Icon = p.icon;
                return (
                  <Reveal key={i} delay={i * 100}>
                    <div className="flex items-center gap-4 rounded-[12px] border border-white/10 bg-white/[0.05] p-4">
                      <span className="shrink-0 h-10 w-10 rounded-full border border-white/20 bg-white/[0.06] flex items-center justify-center">
                        <Icon size={17} strokeWidth={1.75} className="text-[#ee2e24]" />
                      </span>
                      <p className="text-[14px] text-[#ececec] leading-relaxed">{p.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stats (shared band) */}
        <StatsBand />

        {/* FAQ + schema */}
        <FAQSection heading={SERVICES_FAQ.heading} items={SERVICES_FAQ.items} injectSchema />

        {/* CTA band */}
        <section data-testid="services-cta" className="bg-[#fafafa] border-t border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838]">{S.cta.heading}</h2>
            <p className="mt-4 text-[15px] text-[#6e6e6e] leading-relaxed">{S.cta.sub}</p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/#visibility-audit"
                data-testid="services-cta-primary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
              >
                {S.cta.primaryText}
              </Link>
              <a
                href={S.cta.secondaryHref}
                data-testid="services-cta-secondary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] border border-[#d9d9d9] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#383838] hover:border-[#ee2e24] hover:text-[#ee2e24] transition-colors duration-150"
              >
                {S.cta.secondaryText}
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
